import { useState } from 'react'

import AddLink from '@mui/icons-material/AddLink'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LinkOff from '@mui/icons-material/LinkOff'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import StyledTable from '@components/StyledTable'
import type { Entity } from '@models/Entity'
import {
  isUserAannemerForProject,
  isUserVerantwoordelijkForProject,
} from '@util/project-util'

import { useSaveEvent as useSaveAansluitingEvent } from '../../../aansluiting/project/hooks/useSaveEvent'
import type { Project } from '../../../aansluiting/project/types/Project'
import type { UnauthorizedProject } from '../../../aansluiting/project/types/UnauthorizedProject'
import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import { useSaveEvent as useSaveHoofdleidingEvent } from '../../../hoofdleiding/project/hooks/useSaveEvent'
import type { HoofdleidingProject } from '../../../hoofdleiding/project/types/HoofdleidingProject'
import type { UnauthorizedHoofdleidingProject } from '../../../hoofdleiding/project/types/UnauthorizedHoofdleidingProject'
import { PageSection } from '../../../theme/components/PageSection'
import { TableError } from '../../../theme/components/TableError'
import TableLoadingBar from '../../../theme/components/TableLoadingBar'
import type { EnhancedLinkedProject } from '../types/EnhancedLinkedProject'
import { groupProjectAfhankelijkheden } from '../util/linked-projects-util'

import { LinkedProjectRow } from './LinkedProjectRow'
import { LinkedProjectsLegend } from './LinkedProjectsLegend'
import { UnauthorizedLinkedProjectRow } from './UnauthorizedLinkedProjectRow'

export { LinkedProjects }

const headerCells = [
  { id: 'aanvraagID', label: 'Project ID' },
  { id: 'uitvoerdatum', label: 'Geplande start uitvoering' },
  { id: 'doorlooptijdWeken', label: 'Doorlooptijd (in weken)' },
  { id: 'netbeheerders', label: 'Netbeheerders' },
  { id: 'aannemers', label: 'Aannemers' },
  { id: 'statusText', label: 'Status' },
  { id: 'acties', label: 'Acties', fixed: 100 },
] as const

type Props = {
  user: AuthenticatedUser
  parent: Entity
  projects: EnhancedLinkedProject[]
  projectGroups: { _id: string; projects: string[] }[]
  isLoading: boolean
  isError: ConstrainBoolean
}

const LinkedProjects = ({
  user,
  parent,
  projects,
  projectGroups,
  isLoading,
  isError,
}: Props) => {
  const { mutate: saveAansluitingEvent, isPending: isSavingAansluitingEvent } =
    useSaveAansluitingEvent()
  const {
    mutate: saveHoofdleidingEvent,
    isPending: isSavingHoofdleidingEvent,
  } = useSaveHoofdleidingEvent()

  const [dialog, setDialog] = useState<'link' | 'unlink' | null>(null)
  const [errors, setErrors] = useState({ linkId: false })
  const [unlinkId, setUnlinkId] = useState('')
  const [linkId, setLinkId] = useState('')

  const groups = groupProjectAfhankelijkheden({ projects, projectGroups })

  const noResultsFound =
    !isLoading &&
    !groups.some((g) => g.hlProject) &&
    !groups.some((g) => g.alProjecten && g.alProjecten.length > 0) &&
    !groups.some((g) => g.groupedAlProjecten && g.groupedAlProjecten.length > 0)

  const currentProject = projects.find((p) => p.aanvraagID === parent.id)
  const isAannemer = currentProject
    ? isUserAannemerForProject({
        project: currentProject as Project,
        user,
      })
    : false
  const isVerantwoordelijke = currentProject
    ? isUserVerantwoordelijkForProject({
        project: currentProject as Project,
        user,
      })
    : false

  const canLinkProject = (project: EnhancedLinkedProject): boolean => {
    if (!currentProject) {
      return false
    }

    const { type: currentProjectType, aanvraagID: currentAanvraagID } =
      currentProject
    const { aanvraagID: linkingAanvraagID } = project

    if (currentAanvraagID !== linkingAanvraagID) {
      return false
    }

    const canKoppelFromHoofdleiding =
      currentProjectType === 'Hoofdleiding' && user.canKoppelProject
    const canCreateGroupFromAanvraag =
      currentProjectType === 'Aanvraag' &&
      (user.canCreateProjectGroup || user.canKoppelProject)

    return (
      (isVerantwoordelijke || isAannemer) &&
      (canKoppelFromHoofdleiding || canCreateGroupFromAanvraag)
    )
  }

  const canUnlinkProject = (project: EnhancedLinkedProject): boolean => {
    if (!currentProject) {
      return false
    }

    const { type: currentProjectType, aanvraagID: currentAanvraagID } =
      currentProject
    const { type: linkingProjectType, aanvraagID: unlinkingAanvraagID } =
      project

    if (
      !['Aanvraag', 'UnauthorizedAanvraag'].includes(linkingProjectType) ||
      !(isVerantwoordelijke || isAannemer)
    ) {
      return false
    }

    /* v8 ignore next - TODO: missing coverage -- @preserve */
    const isLinkedToCurrentProjectButNotInGroup =
      !projectGroups.some((g) => g.projects.includes(project.aanvraagID)) &&
      !!currentProject.linkedProjects?.includes(project.aanvraagID)

    const isInGroupWithCurrentProject = projectGroups.some(
      (g) =>
        g.projects.includes(currentProject.aanvraagID) &&
        g.projects.includes(project.aanvraagID),
    )

    if (currentProjectType === 'Hoofdleiding') {
      return (
        user.canKoppelProject &&
        (isLinkedToCurrentProjectButNotInGroup || isInGroupWithCurrentProject)
      )
    }

    return (
      currentProjectType === 'Aanvraag' &&
      user.canCreateProjectGroup &&
      currentAanvraagID !== unlinkingAanvraagID &&
      isInGroupWithCurrentProject
    )
  }

  const validate = () => {
    setErrors({ linkId: !linkId })

    return !!linkId
  }

  const openLinkDialog = () => {
    setLinkId('')
    setDialog('link')
  }

  const openUnlinkDialog = (id: string) => {
    setUnlinkId(id)
    setDialog('unlink')
  }

  const closeDialog = () => {
    setDialog(null)
  }

  const onLink = () => {
    if (validate()) {
      // Koppel aanvraag aan hoofdleiding
      if (parent.projecttype === 'Aanvraag' && linkId.startsWith('3')) {
        saveAansluitingEvent(
          {
            id: linkId,
            data: {
              addProject: parent.id,
            },
            type: 'project_koppelen',
            source: 'manual',
          },
          {
            onSuccess: () => {
              setDialog(null)
            },
          },
        )
        // Koppel aanvraag aan aanvraag
      } else if (parent.projecttype === 'Aanvraag') {
        saveAansluitingEvent(
          {
            id: parent.id,
            data: {
              addProject: linkId,
            },
            type: 'update_project_group',
            source: 'manual',
          },
          {
            onSuccess: () => {
              setDialog(null)
            },
          },
        )
      } else if (parent.projecttype === 'Hoofdleiding') {
        saveHoofdleidingEvent(
          {
            id: parent.id,
            data: {
              addProject: linkId,
            },
            type: 'project_koppelen',
            source: 'manual',
          },
          {
            onSuccess: () => {
              setDialog(null)
            },
          },
        )
      }
    }
  }

  const onUnlink = () => {
    if (parent.projecttype === 'Aanvraag') {
      saveAansluitingEvent(
        {
          id: parent.id,
          data: {
            deleteProject: unlinkId,
          },
          type: 'update_project_group',
          source: 'manual',
        },
        {
          onSuccess: () => {
            setDialog(null)
          },
        },
      )
    }

    if (parent.projecttype === 'Hoofdleiding') {
      saveHoofdleidingEvent(
        {
          id: parent.id,
          data: {
            deleteProject: unlinkId,
          },
          type: 'project_koppelen',
          source: 'manual',
        },
        {
          onSuccess: () => {
            setDialog(null)
          },
        },
      )
    }
  }

  return (
    <PageSection heading="Projectafhankelijkheden" id="projectafhankelijkheden">
      <Box>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Alle projectafhankelijkheden</Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ padding: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <LinkedProjectsLegend />

              <StyledTable
                headerCells={headerCells}
                variant="details"
                loading={isLoading}
                isError={isError}
                provideBody={false}
                {...({} as any)}
              >
                {noResultsFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={headerCells.length}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          Geen projectafhankelijkheden gevonden.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}

                {(isLoading || isError) && (
                  <TableBody>
                    {isLoading && (
                      <TableLoadingBar colSpan={headerCells.length} />
                    )}
                    {isError && <TableError colSpan={headerCells.length} />}
                  </TableBody>
                )}

                {groups.map((group) => {
                  const isGroupedWithHl =
                    !!group.hlProject &&
                    (group.groupedAlProjecten.length > 0 ||
                      group.alProjecten.length > 0)

                  return (
                    <TableBody
                      key={group.hlProject?.aanvraagID || 1}
                      sx={{
                        border: isGroupedWithHl
                          ? '2px dashed rgba(0,0,0,.25)'
                          : undefined,

                        '& > tr:nth-of-type(odd)': {
                          background: 'rgba(0,0,0,.02)',
                        },
                      }}
                    >
                      {group.hlProject && (
                        <LinkedProjectRow
                          key={group.hlProject.aanvraagID}
                          entity={parent}
                          canLink={canLinkProject(group.hlProject)}
                          canUnlink={canUnlinkProject(group.hlProject)}
                          project={group.hlProject as HoofdleidingProject}
                          openUnlinkDialog={openUnlinkDialog}
                          openLinkDialog={openLinkDialog}
                        />
                      )}

                      {group.groupedAlProjecten.map((group) => {
                        return group.map((project, index, arr) => {
                          const isFirst = index === 0
                          const isLast = index === arr.length - 1

                          if (
                            project.enhancedType === 'Hoofdleiding' ||
                            project.enhancedType === 'Aanvraag'
                          ) {
                            return (
                              <LinkedProjectRow
                                key={project.aanvraagID}
                                entity={parent}
                                canLink={canLinkProject(project)}
                                canUnlink={canUnlinkProject(project)}
                                project={
                                  project as Project | HoofdleidingProject
                                }
                                openUnlinkDialog={openUnlinkDialog}
                                openLinkDialog={openLinkDialog}
                                sx={{
                                  position: 'relative',

                                  '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: isFirst ? '40%' : 0,
                                    bottom: isLast ? '40%' : 0,
                                    width: '4px',
                                    backgroundColor: 'rgba(0,0,0,.33)',
                                    borderRadius: isFirst
                                      ? '0 4px 0 0'
                                      : isLast
                                        ? '0 0 4px 0'
                                        : 0,
                                  },
                                }}
                              />
                            )
                          }

                          return (
                            <UnauthorizedLinkedProjectRow
                              key={project.aanvraagID}
                              colSpan={headerCells.length}
                              entity={parent}
                              openUnlinkDialog={openUnlinkDialog}
                              canUnlink={canUnlinkProject(project)}
                              project={
                                project as
                                  | UnauthorizedHoofdleidingProject
                                  | UnauthorizedProject
                              }
                              sx={{
                                position: 'relative',

                                '&:after': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  top: isFirst ? '40%' : 0,
                                  bottom: isLast ? '40%' : 0,
                                  width: '4px',
                                  backgroundColor: 'rgba(0,0,0,.33)',
                                  borderRadius: isFirst
                                    ? '0 4px 0 0'
                                    : isLast
                                      ? '0 0 4px 0'
                                      : 0,
                                },
                              }}
                            />
                          )
                        })
                      })}

                      {group.alProjecten.map((project) => {
                        if (
                          project.enhancedType === 'Hoofdleiding' ||
                          project.enhancedType === 'Aanvraag'
                        ) {
                          return (
                            <LinkedProjectRow
                              key={project.aanvraagID}
                              entity={parent}
                              canLink={canLinkProject(project)}
                              canUnlink={canUnlinkProject(project)}
                              project={project as Project | HoofdleidingProject}
                              openUnlinkDialog={openUnlinkDialog}
                              openLinkDialog={openLinkDialog}
                            />
                          )
                        }

                        return (
                          <UnauthorizedLinkedProjectRow
                            key={project.aanvraagID}
                            colSpan={headerCells.length}
                            entity={parent}
                            openUnlinkDialog={openUnlinkDialog}
                            canUnlink={canUnlinkProject(project)}
                            project={
                              project as
                                | UnauthorizedHoofdleidingProject
                                | UnauthorizedProject
                            }
                          />
                        )
                      })}
                    </TableBody>
                  )
                })}
              </StyledTable>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Dialog open={dialog === 'link'} fullWidth maxWidth="sm">
        <DialogTitle>Project koppelen aan {parent.id}</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            variant="standard"
            value={linkId}
            onChange={(e) => setLinkId(e.target.value.trim())}
            error={errors.linkId}
            label="Project ID"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog} autoFocus>
            Annuleren
          </Button>

          <Button
            onClick={onLink}
            variant="contained"
            endIcon={<AddLink />}
            loading={isSavingAansluitingEvent || isSavingHoofdleidingEvent}
          >
            Projecten koppelen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === 'unlink'} fullWidth maxWidth="sm">
        <DialogTitle>Project ontkoppelen van {parent.id}</DialogTitle>

        <DialogContent>
          <Typography>
            Wilt u project {unlinkId} ontkoppelen van {parent.id}?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog} autoFocus>
            Annuleren
          </Button>

          <Button
            onClick={onUnlink}
            variant="contained"
            endIcon={<LinkOff />}
            loading={isSavingAansluitingEvent || isSavingHoofdleidingEvent}
          >
            Projecten ontkoppelen
          </Button>
        </DialogActions>
      </Dialog>
    </PageSection>
  )
}
